import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, divIcon } from 'leaflet';
import { Star, ExternalLink, MapPin } from 'lucide-react';
import { Route, UserLocation } from '../types';
// Removed calculateDistance and formatDistance imports - no longer needed

interface RouteMapProps {
  routes: Route[];
  userLocation?: UserLocation;
  selectedRoutes?: Route[];
  onRouteSelect?: (route: Route) => void;
  selectedRoute?: Route | null;
  className?: string;
}

// Custom hook to fit map bounds to routes
function MapUpdater({ routes }: { routes: Route[] }) {
  const map = useMap();
  
  useEffect(() => {
    if (routes.length > 0) {
      // Calculate bounds of all routes
      const lats = routes.map(route => route['Area Latitude']);
      const lons = routes.map(route => route['Area Longitude']);
      
      const bounds: [[number, number], [number, number]] = [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)]
      ];
      
      // Fit map to show all routes with some padding
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, routes]);
  
  return null;
}

// Custom hook to auto-open popup for selected route
function SelectedRouteUpdater({ selectedRoute }: { selectedRoute: Route | null | undefined }) {
  const map = useMap();
  
  useEffect(() => {
    if (selectedRoute) {
      // Center map on selected route
      map.setView([selectedRoute['Area Latitude'], selectedRoute['Area Longitude']], 15);
      
      // Find the marker for the selected route and open its popup
      setTimeout(() => {
        map.eachLayer((layer: any) => {
          if (layer.getLatLng) {
            const latLng = layer.getLatLng();
            if (latLng.lat === selectedRoute['Area Latitude'] && latLng.lng === selectedRoute['Area Longitude']) {
              layer.openPopup();
            }
          }
        });
      }, 500); // Small delay to ensure marker is rendered
    }
  }, [map, selectedRoute]);
  
  return null;
}

export default function RouteMap({ 
  routes, 
  userLocation, 
  selectedRoutes = [], 
  onRouteSelect,
  selectedRoute,
  className = '' 
}: RouteMapProps) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Create simple colored circle icons using divIcon to avoid btoa issues
  const createCustomIcon = (color: string) => divIcon({
    html: `<div style="
      background-color: ${color};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    className: 'custom-div-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  const routeIcon = createCustomIcon('#3b82f6'); // Blue for routes
  const selectedRouteIcon = createCustomIcon('#ef4444'); // Red for selected routes

  // Calculate center based on routes, not user location
  const calculateMapCenter = (): [number, number] => {
    if (routes.length === 0) {
      return [37.65, -83.75]; // Default Red River Gorge center
    }
    
    // Calculate bounds of all routes
    const lats = routes.map(route => route['Area Latitude']);
    const lons = routes.map(route => route['Area Longitude']);
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    
    // Center on the routes
    return [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
  };

  const center = calculateMapCenter();

  if (!isClient) {
    return (
      <div className={`bg-gray-200 rounded-lg flex items-center justify-center ${className}`} style={{ height: '600px' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`} style={{ height: '600px' }}>
      <MapContainer
        center={center}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapUpdater routes={routes} />
        <SelectedRouteUpdater selectedRoute={selectedRoute} />
        
        {/* User location marker removed */}
        
        {/* Route markers */}
        {routes.map((route) => {
          const isSelected = selectedRoutes.some(selected => selected.Route === route.Route);

          return (
            <Marker
              key={route.Route}
              position={[route['Area Latitude'], route['Area Longitude']]}
              icon={isSelected ? selectedRouteIcon : routeIcon}
              eventHandlers={{
                click: () => onRouteSelect?.(route),
              }}
            >
            <Popup>
              <div className="min-w-[280px] max-w-[320px]">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-800 mb-1">{route.Route}</h3>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span className="truncate">{route.Location.split(' > ').slice(-2, -1)[0] || route.Location.split(' > ').slice(-2)[0]}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Route Details */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Grade:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        String(route.Rating).toLowerCase().includes('5.6') || String(route.Rating).toLowerCase().includes('5.7') ? 'bg-green-100 text-green-800' :
                        String(route.Rating).toLowerCase().includes('5.8') || String(route.Rating).toLowerCase().includes('5.9') ? 'bg-yellow-100 text-yellow-800' :
                        String(route.Rating).toLowerCase().includes('5.10') ? 'bg-orange-100 text-orange-800' :
                        String(route.Rating).toLowerCase().includes('5.11') ? 'bg-red-100 text-red-800' :
                        String(route.Rating).toLowerCase().includes('5.12') ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {route.Rating}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Type:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        route['Route Type'] === 'Sport' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {route['Route Type']}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Length:</span>
                      <span className="text-sm font-medium">{route.Length} ft</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Pitches:</span>
                      <span className="text-sm font-medium">{route.Pitches}</span>
                    </div>
                  </div>

                  {/* Quality and Distance */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Quality:</span>
                      <div className="flex items-center">
                        {route['Avg Stars'] && route['Avg Stars'] > 0 ? (
                          <>
                            {Array.from({ length: Math.floor(route['Avg Stars']) }, (_, i) => (
                              <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            ))}
                            {route['Avg Stars'] % 1 >= 0.5 && (
                              <Star className="w-3 h-3 fill-yellow-400/50 text-yellow-400" />
                            )}
                            {Array.from({ length: Math.max(0, 4 - Math.ceil(route['Avg Stars'])) }, (_, i) => (
                              <Star key={`empty-${i}`} className="w-3 h-3 text-gray-300" />
                            ))}
                            <span className="ml-1 text-xs font-medium">({route['Avg Stars']})</span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">No rating</span>
                        )}
                      </div>
                    </div>
                    
                  </div>
                </div>

                <div className="border-t pt-3">
                  <a 
                    href={route.URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View on Mountain Project
                  </a>
                </div>
              </div>
            </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
